import React from 'react'
import clsx from 'clsx'
import { Play, Pause, CheckCircle } from 'lucide-react'
import type { Session } from '../../types/session.types'
import { SessionTimer } from './SessionTimer'
import { SessionStatusBadge } from './SessionStatusBadge'
import {
  useStartSession,
  usePauseSession,
  useCompleteSession,
} from '../../hooks/useSessionMutations'
import { useCanStartSession } from '../../stores/selectors'
import styles from './SessionHeader.module.css'

export interface SessionHeaderProps {
  session: Session
  className?: string
}

export function SessionHeader({ session, className }: SessionHeaderProps) {
  const startMutation = useStartSession(session.id)
  const pauseMutation = usePauseSession(session.id)
  const completeMutation = useCompleteSession(session.id)
  const canStart = useCanStartSession()

  const envColors: Record<string, string> = {
    production: styles.envProduction,
    staging: styles.envStaging,
    rehearsal: styles.envRehearsal,
  }

  const envEmoji =
    session.environment === 'production'
      ? '🔴'
      : session.environment === 'staging'
      ? '🟡'
      : '🔵'

  return (
    <header className={clsx(styles.header, envColors[session.environment], className)}>
      {/* Left: Session Info */}
      <div className={styles.info}>
        <span className={styles.envBadge}>
          {envEmoji} {session.environment.toUpperCase()}
        </span>
        <h1 className={styles.name} title={session.name}>
          {session.name}
        </h1>
        <span className={styles.system}>{session.system_name}</span>
        <SessionStatusBadge status={session.status} />
      </div>

      {/* Center: Timer */}
      <div className={styles.center}>
        <SessionTimer
          plannedStart={session.planned_start}
          plannedEnd={session.planned_end}
          actualStart={session.actual_start}
          status={session.status}
        />
      </div>

      {/* Right: Controls */}
      <div className={styles.controls}>
        {session.status === 'ready' && canStart && (
          <button
            id="session-start-btn"
            className={clsx(styles.btn, styles.btnStart)}
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            aria-label="Start session"
          >
            <Play size={14} />
            {startMutation.isPending ? 'Starting…' : 'START'}
          </button>
        )}

        {session.status === 'in_progress' && (
          <>
            <button
              id="session-pause-btn"
              className={clsx(styles.btn, styles.btnPause)}
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              aria-label="Pause session"
            >
              <Pause size={14} />
              PAUSE
            </button>
            <button
              id="session-complete-btn"
              className={clsx(styles.btn, styles.btnComplete)}
              onClick={() => {
                if (window.confirm('Mark session as COMPLETED?')) {
                  completeMutation.mutate()
                }
              }}
              disabled={completeMutation.isPending}
              aria-label="Complete session"
            >
              <CheckCircle size={14} />
              COMPLETE
            </button>
          </>
        )}

        {session.status === 'paused' && (
          <button
            id="session-resume-btn"
            className={clsx(styles.btn, styles.btnStart)}
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            aria-label="Resume session"
          >
            <Play size={14} />
            RESUME
          </button>
        )}
      </div>
    </header>
  )
}
